// Schnittstelle zur Datenhaltung. Zwei Umsetzungen:
//  - demoQuelle:     alles im Browser (Vorführung, Tests, ohne Server)
//  - supabaseQuelle: Supabase-Datenbank (Produktivbetrieb)
import type { Stand, Id, EinsatzEntwurf, Abwesenheit, Kurs, Unterrichtsform, Datum } from './typen';
import { type Regelverstoss, fehlerText } from './regeln';

export class RegelFehler extends Error {
  constructor(public verstoss: Regelverstoss) { super(fehlerText(verstoss)); }
}

/** Tabellen, die über die allgemeinen Funktionen speichern/loeschen gepflegt werden */
export type Tabelle =
  | 'dozent' | 'fachbereich' | 'lehrgang_vorlage' | 'vorlage_knoten' | 'standort' | 'raum'
  | 'zeitraster' | 'zeitfenster' | 'kalender_tag' | 'kurs' | 'kurs_knoten' | 'mandant';

export const TABELLE_ZU_STAND: Record<Tabelle | 'einsatz' | 'abwesenheit' | 'absage' | 'dozent_qualifikation', keyof Stand> = {
  mandant: 'mandant', dozent: 'dozenten', fachbereich: 'fachbereiche', lehrgang_vorlage: 'vorlagen', vorlage_knoten: 'vorlageKnoten',
  standort: 'standorte', raum: 'raeume', zeitraster: 'zeitraster', zeitfenster: 'zeitfenster', kalender_tag: 'kalender',
  kurs: 'kurse', kurs_knoten: 'kursKnoten', einsatz: 'einsaetze', abwesenheit: 'abwesenheiten', absage: 'absagen',
  dozent_qualifikation: 'qualifikationen',
};

export interface NeuerKurs {
  vorlage_id: Id; bezeichnung: string; kennung: string; beginn: Datum; ende: Datum;
  standort_id: Id | null; standard_raum_id: Id | null; zeitraster_id: Id | null; standard_form: Unterrichtsform; farbe?: string;
}
export type AbwesenheitEingabe = Omit<Abwesenheit, 'id' | 'mandant_id'> & { id?: Id };

export interface Datenquelle {
  art: 'demo' | 'supabase';
  /** 'admin' = Planungsoberfläche, 'dozent' = Dozenten-App (eigene Daten) */
  rolle: 'admin' | 'dozent';
  laden(): Promise<Stand>;
  /** Meldet Änderungen anderer Benutzer (Echtzeit). Rückgabe: Abmelden */
  abonnieren(beiAenderung: () => void): () => void;

  einsatzSpeichern(e: EinsatzEntwurf): Promise<void>;
  /** Legt mehrere Entwürfe an, überspringt Termine mit Regelverstoß. Rückgabe: Anzahl angelegt */
  einsaetzeAnlegen(liste: EinsatzEntwurf[]): Promise<number>;
  einsatzLoeschen(id: Id): Promise<void>;
  freigeben(ids: Id[]): Promise<void>;

  abwesenheitSpeichern(a: AbwesenheitEingabe): Promise<void>;
  abwesenheitLoeschen(id: Id): Promise<void>;
  absageErledigen(id: Id): Promise<void>;
  /** Dozent sagt einen eigenen freigegebenen Einsatz ab */
  einsatzAbsagen(id: Id, grund: string | null): Promise<void>;
  /** Admin lädt einen Dozenten zur Dozenten-App ein (E-Mail mit Anmeldelink) */
  dozentEinladen(dozentId: Id): Promise<void>;

  kursAnlegen(k: NeuerKurs): Promise<Id>;
  qualifikationSetzen(dozentId: Id, fachbereichId: Id, an: boolean): Promise<void>;
  speichern(tabelle: Tabelle, zeile: { id?: Id; [feld: string]: unknown }): Promise<Id>;
  loeschen(tabelle: Tabelle, id: Id): Promise<void>;

  /** Push-Nachrichten bei Absagen für dieses Gerät aktivieren (nur Betrieb mit Supabase) */
  pushAktivieren?(): Promise<'aktiv' | 'abgelehnt'>;
  pushStatus?(): Promise<'aktiv' | 'aus' | 'nicht-moeglich'>;

  /** nur Demo: Ausgangsdaten wiederherstellen */
  zuruecksetzen?(): Promise<void>;
}

export type { Kurs };
